import { ChakraBox } from '@equip.health/ui';
import type { DateTime } from 'luxon';

import ScheduleView from '~/components/common/calendar/schedule/ScheduleView';
import OverviewLoading from '~/components/common/OverviewLoading';
import { ScheduleMode } from '~/lib/constants/schedule.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  getScheduleEventsFromGroupClassEvents,
  getScheduleItemFromExternalId,
} from '~/lib/util/schedule/schedule.util';
import {
  useGetGroupClassEventsQuery,
  useRemoveGroupClassEventMutation,
} from '~/store/services/groupClass';

import GroupClassEventDetailsModal from './modals/GroupClassEventDetailsModal';
import { useLazyGetGroupClassNotesByEventExternalIdsQuery } from '~/store/services/notes/notes';
import { useMemo, useState } from 'react';
import { useConsolidatedScheduleItems } from '~/lib/hooks/useConsolidatedScheduleItems';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface GroupClassWeekViewProps {
  currentDate: DateTime;
}

const GroupClassWeekView = ({ currentDate }: GroupClassWeekViewProps) => {
  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const [isEventDetailsModalOpen, setIsEventDetailsModalOpen] =
    useState<boolean>(false);

  const [
    selectedGroupClassEventExternalId,
    setSelectedGroupClassEventExternalId,
  ] = useState<string>('');

  const [selectedGroupEvent, setSelectedGroupEvent] =
    useState<ScheduleItemBase>(null);

  const {
    currentUser: { userId },
  } = useGlobalContext();

  const dateFilter = useMemo(() => {
    return {
      startDate: currentDate
        .startOf('week')
        .minus({ days: 1 })
        .toJSDate()
        .toISOString(),
      endDate: currentDate
        .endOf('week')
        .minus({ days: 1 })
        .toJSDate()
        .toISOString(),
    };
  }, [currentDate]);

  const { data: groupClassEventDetails, isFetching } =
    useGetGroupClassEventsQuery(
      {
        endDateTime: dateFilter.endDate,
        pageSize: 200,
        startDateTime: dateFilter.startDate,
      },
      {
        skip: isGroupsRestructuringEnabled,
      },
    );

  const [removeGroupClassEvent] = useRemoveGroupClassEventMutation();

  const [fetchGroupClassEventNotes] =
    useLazyGetGroupClassNotesByEventExternalIdsQuery();

  const closeEventDetailsModal = (): void => {
    setIsEventDetailsModalOpen(false);
  };

  const onRemoveEvent = (
    groupClassEventExternalId: string,
    isRemoveEvent?: boolean,
  ): void => {
    if (isRemoveEvent) {
      removeGroupClassEvent(groupClassEventExternalId);
    }
    closeEventDetailsModal();
  };

  const scheduledItems = useMemo(
    () =>
      getScheduleEventsFromGroupClassEvents(
        groupClassEventDetails?.data || [],
        userId,
        [],
      ),
    [groupClassEventDetails?.data, userId],
  );

  const { consolidatedScheduleItems, isLoading } = useConsolidatedScheduleItems(
    {
      scheduleItems: scheduledItems,
      filterByValue: userId,
      startDateTime: dateFilter.startDate,
      endDateTime: dateFilter.endDate,
      pageSize: 200,
    },
  );

  const openSelectedEventDetails = (
    scheduleItem: ScheduleItemBase,
    externalId: string,
  ): void => {
    setSelectedGroupEvent(scheduleItem);
    setIsEventDetailsModalOpen(true);
    setSelectedGroupClassEventExternalId(
      externalId ?? scheduleItem.groupClassExternalId,
    );
  };

  const getSelectedEvent = (externalId: string) => {
    if (isGroupsRestructuringEnabled) {
      openSelectedEventDetails(
        getScheduleItemFromExternalId(consolidatedScheduleItems, externalId),
        externalId,
      );
      return;
    } else {
      // TODO: Remove this logic when we remove the old group class flow
      fetchGroupClassEventNotes([externalId])
        .unwrap()
        .then((groupClassEventNotes) => {
          const scheduleItems = getScheduleEventsFromGroupClassEvents(
            groupClassEventDetails?.data || [],
            userId,
            groupClassEventNotes,
          );
          openSelectedEventDetails(
            getScheduleItemFromExternalId(scheduleItems, externalId),
            externalId,
          );
        });
    }
  };

  if (isFetching || isLoading) return <OverviewLoading />;

  return (
    <>
      <ChakraBox paddingRight="24px">
        <ScheduleView
          currentDate={currentDate}
          height="calc(100vh - 198px)"
          mode={ScheduleMode.WEEK}
          onCloseEventDetailsModal={closeEventDetailsModal}
          onScheduleItemClicked={(externalId: string) => {
            getSelectedEvent(externalId);
          }}
          scheduledItems={consolidatedScheduleItems}
        />
        <GroupClassEventDetailsModal
          groupClassEventExternalId={selectedGroupClassEventExternalId}
          id="group-class-event-details-modal"
          isOpen={isEventDetailsModalOpen}
          onClose={closeEventDetailsModal}
          onRemoveEvent={onRemoveEvent}
          scheduleItem={selectedGroupEvent}
          title="Event Details"
        />
      </ChakraBox>
    </>
  );
};

export default GroupClassWeekView;
